import type { Vec3 } from "@rietz/shared";

/**
 * The maths of a peek: a held INPUT (−1 / 0 / +1) rendered as a continuous
 * camera offset. Here rather than in `loop.ts` so the ease can be tested for
 * the property that matters — FRAMERATE-INDEPENDENCE; a `t += (target − t) *
 * 0.2` per frame eases three times faster at 360 Hz. `LEAN_OFFSET_M` is where
 * the EYE goes; the head box in `sim/hitbox.ts` moves 0.26 m — close on
 * purpose, independent on purpose.
 */

/** How far the eye slides sideways at full lean, metres. */
export const LEAN_OFFSET_M = 0.32;

/**
 * How far the eye DROPS at full lean, metres. Small, and what stops the offset
 * reading as a sideways teleport: a leaning head describes an arc. Applied to
 * |lean|.
 */
export const LEAN_DIP_M = 0.06;

/**
 * Camera roll at full lean, radians (~4.6°) — far less than the rig's ~10°:
 * this is the player's own horizon, where a little goes a long way.
 */
export const LEAN_ROLL_RAD = 0.08;

/**
 * Time constant of the ease, ms. ~95 % in 3τ ≈ 135 ms: a key press, not an
 * animation, and spamming Q/E does not strobe the horizon.
 */
export const LEAN_TAU_MS = 45;

/**
 * Eases `current` toward `target` by `dtMs`, framerate-independently: `1 −
 * exp(−dt/τ)` covers a fraction that depends on elapsed TIME. Snaps to the
 * target inside a millimetre, so `leanRoll(0)` really is 0.
 */
export function easeLean(current: number, target: number, dtMs: number): number {
  if (dtMs <= 0) return current;
  const next = current + (target - current) * (1 - Math.exp(-dtMs / LEAN_TAU_MS));
  return Math.abs(target - next) < 1e-4 ? target : next;
}

/**
 * Adds a lean's lateral offset and dip to a position, in place. Right is
 * `forward × up` = `(cos, 0, −sin)`; a positive `leanT` goes along +right,
 * which at yaw 0 is world +X — the same +X the hitboxes slide along.
 */
export function applyLeanOffset(pos: Vec3, yaw: number, leanT: number): void {
  if (leanT === 0) return;
  pos.x += Math.cos(yaw) * leanT * LEAN_OFFSET_M;
  pos.z += -Math.sin(yaw) * leanT * LEAN_OFFSET_M;
  pos.y -= Math.abs(leanT) * LEAN_DIP_M;
}

/**
 * The camera's z-rotation for a lean — the roll, in radians. NEGATIVE for a lean
 * to the right: with rotation order "YXZ", `rotation.z = φ` takes the camera's
 * up to `(−sin φ, cos φ, 0)`, and tilting your head right by α takes your up to
 * `(+sin α, cos α, 0)`, so φ = −α. The check that survives a refactor: with
 * φ < 0 the horizon tilts up to the right, as when your right ear goes to your
 * shoulder.
 */
export function leanRoll(leanT: number): number {
  return -leanT * LEAN_ROLL_RAD;
}
